import React, { useMemo, useState } from 'react';
import { Battery, Boxes, ChevronRight, MapPin, Radio, Smartphone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import SoverdiSiteSelector from '../../soverdi-clone/components/SoverdiSiteSelector';
import { useAgriSafeSoverdiDashboard } from '../../soverdi-clone/hooks/useAgriSafeSoverdiDashboard';
import { useFieldBindings } from '../../hooks/useFieldBindings';
import { useCgmDisplayMode } from '../../contexts/CgmDisplayModeContext';
import { getNodeBattery, isRelayCapable, isValveCapable } from '../../lib/liveSensorMetrics';
import { isNodeOnline, nodeTimestamp } from '../../lib/operationalOverview';
import type { IoTNode } from '../../lib/iotApi';
import {
  equipmentMetrics,
  formatEquipmentValue,
} from '../../soverdi-clone/adapters/equipmentPresentation';

type EquipmentFilter = 'all' | 'pressure' | 'soil' | 'flow' | 'valves' | 'motor' | 'control' | 'other';
type EquipmentFamily = Exclude<EquipmentFilter, 'all'>;
type TelemetryState = 'online' | 'stale' | 'unknown';

const FILTERS: EquipmentFilter[] = ['all', 'pressure', 'soil', 'flow', 'valves', 'motor', 'control', 'other'];
const TELEMETRY_ONLINE: TelemetryState = 'online';
const TELEMETRY_STALE: TelemetryState = 'stale';
const TELEMETRY_UNKNOWN: TelemetryState = 'unknown';

const equipmentFamilies = (node: IoTNode): EquipmentFamily[] => {
  const model = (node.model || '').trim().toLowerCase();
  if (isValveCapable(node.model)) {
    const families: EquipmentFamily[] = ['valves'];
    if (model === 'asvwf01' || model === 'valve_water_flow') families.push('flow');
    return families;
  }
  if (model.includes('pressure')) return ['pressure'];
  if (model.includes('soil') || model.includes('ecth') || model.includes('ec-th')) return ['soil'];
  if (model.includes('flow')) return ['flow'];
  if (model.includes('ct') || model.includes('motor') || model.includes('pump') || model.includes('vfd') || model.includes('current')) return ['motor'];
  if (isRelayCapable(node.model) || model.includes('relay')) return ['control'];
  return ['other'];
};

const equipmentTelemetryState = (node: IoTNode, now: number): TelemetryState => {
  if (nodeTimestamp(node) === null) return TELEMETRY_UNKNOWN;
  return isNodeOnline(node, now) ? TELEMETRY_ONLINE : TELEMETRY_STALE;
};

const CgmEquipmentR1: React.FC = () => {
  const { t } = useTranslation(['common', 'agrisafeR1']);
  const { isExpert } = useCgmDisplayMode();
  const {
    topology,
    parcelDisplayName,
    nodes,
    nodesLoaded,
    placement,
    siteModels,
    loading,
    loadError,
    bindingsUnavailable,
  } = useAgriSafeSoverdiDashboard();
  const {
    parcelBindings,
    farmId: bindingsFarmId,
    pressureSensorByBinding,
    pumps,
    loaded: fieldBindingsLoaded,
    error: fieldBindingsError,
  } = useFieldBindings();
  const installedNamesForNode = (nodeId: string): string => bindingsFarmId === topology?.farmId
    ? parcelBindings.filter(binding => binding.controller_node_id === nodeId || pressureSensorByBinding?.[binding.binding_id] === nodeId)
      .map(binding => parcelDisplayName?.(binding) ?? binding.parcel).join(' · ') : '';
  const [selectedSiteName, setSelectedSiteName] = useState('');
  const [filter, setFilter] = useState<EquipmentFilter>('all');

  const filterLabels: Record<EquipmentFilter, string> = {
    all: t('common:cgmEquipmentAll'),
    pressure: t('common:cgmEquipmentPressure'),
    soil: t('common:cgmEquipmentSoil'),
    flow: t('common:cgmEquipmentFlow'),
    valves: t('common:cgmEquipmentValves'),
    motor: t('common:cgmEquipmentMotor'),
    control: t('common:cgmEquipmentControl'),
    other: t('common:cgmEquipmentOther'),
  };

  const formatAge = (timestamp: number | null): string => {
    if (timestamp === null) return t('common:availabilityNotReported');
    const seconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
    if (seconds < 60) return t('agrisafeR1:dashboard.relative.secondsAgo', { count: seconds });
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return t('agrisafeR1:dashboard.relative.minutesAgo', { count: minutes });
    const hours = Math.floor(minutes / 60);
    return t('agrisafeR1:dashboard.relative.hoursAgo', { count: hours });
  };

  const localizedMetricLabel = (label: string): string => {
    switch (label) {
      case 'Pression': return t('agrisafeR1:dashboard.metrics.pressure');
      case 'Débit': return t('agrisafeR1:dashboard.metrics.flow');
      case 'Humidité sol': return t('agrisafeR1:dashboard.metrics.soilMoisture');
      case 'EC sol': return t('agrisafeR1:dashboard.metrics.soilEc');
      case 'Température sol': return t('agrisafeR1:dashboard.metrics.soilTemperature');
      case 'Température air': return t('agrisafeR1:dashboard.metrics.airTemperature');
      case 'Humidité air': return t('agrisafeR1:dashboard.metrics.airHumidity');
      case 'Batterie': return t('agrisafeR1:dashboard.metrics.battery');
      case 'Vanne': return t('common:cgmEquipmentValves');
      default: return label;
    }
  };

  const localizedMetricValue = (value: string): string => {
    switch (value) {
      case 'Ouverte': return t('agrisafeR1:parcel.state.open');
      case 'Fermée': return t('agrisafeR1:parcel.state.closed');
      case 'Inconnu': return t('agrisafeR1:sectorDetail.equipmentStatus.unknown');
      case 'Activé': return t('common:enabled');
      case 'Désactivé': return t('common:disabled');
      default: return value;
    }
  };

  const commissioningStateLabel = (statuses: string[]): string => {
    if (!fieldBindingsLoaded || fieldBindingsError || bindingsUnavailable) {
      return t('common:availabilityUnavailable');
    }
    const normalized = Array.from(new Set(
      statuses
        .map((status) => status.trim().toUpperCase())
        .filter((status) => status.length > 0),
    ));
    if (normalized.length === 0) return t('common:availabilityNotReported');
    if (normalized.length > 1) return t('agrisafeR1:sectorDetail.equipmentStatus.unknown');
    switch (normalized[0]) {
      case 'COMMISSIONED':
        return t('agrisafeR1:sectorDetail.commandState.confirmed');
      case 'NOT_COMMISSIONED':
      case 'UNCOMMISSIONED':
        return t('common:nonCommissioned');
      case 'READY_FOR_FIELD':
        return t('agrisafeR1:sectorDetail.commandState.ready');
      case 'PENDING':
      case 'TO_CONFIRM':
        return t('agrisafeR1:sectors.toConfirm');
      default:
        return t('agrisafeR1:sectorDetail.equipmentStatus.unknown');
    }
  };

  const commissioningForNode = (node: IoTNode): { label: string; detail: string | null } => {
    const valveBindings = parcelBindings.filter((binding) => binding.controller_node_id === node.id);
    const pumpBindings = pumps.filter((binding) => binding.relay_node_id === node.id);
    const statuses = [
      ...valveBindings.map((binding) => binding.commissioning_status),
      ...pumpBindings.map((binding) => binding.commissioning_status),
    ];
    const label = commissioningStateLabel(statuses);

    if (!fieldBindingsLoaded || fieldBindingsError || bindingsUnavailable) {
      return { label, detail: null };
    }

    const valveChannels = Array.from(new Set(
      valveBindings
        .map((binding) => binding.channel_id)
        .filter((channel) => typeof channel === 'string' && channel.trim().length > 0),
    ));
    const relayChannels = Array.from(new Set(
      pumpBindings
        .map((binding) => binding.relay_channel)
        .filter((channel): channel is string => typeof channel === 'string' && channel.trim().length > 0),
    ));
    const details: string[] = [];
    if (valveChannels.length > 0) {
      details.push(`${t('agrisafeR1:sectorDetail.confirm.channels')}: ${valveChannels.join(', ')}`);
    }
    if (relayChannels.length > 0) {
      details.push(`${t('agrisafeR1:pumping.diagnostic.relayChannel')}: ${relayChannels.join(', ')}`);
    }
    return { label, detail: details.length > 0 ? details.join(' · ') : null };
  };

  const siteNames = useMemo(() => topology?.sites.map((site) => site.name) ?? [], [topology]);
  const effectiveSiteName = selectedSiteName || siteNames[0] || '';

  const nodeSite = useMemo(() => {
    const map = new Map<string, string>();
    if (!nodesLoaded) return map;
    for (const node of nodes) {
      if (node.sector_id) {
        const located = placement.get(node.sector_id);
        if (located) map.set(node.id, located.siteName);
      }
    }
    for (const site of siteModels) {
      if (site.relayNode) map.set(site.relayNode.id, site.siteName);
    }
    return map;
  }, [nodes, nodesLoaded, placement, siteModels]);

  const siteNodes = useMemo(
    () => nodesLoaded ? nodes.filter((node) => nodeSite.get(node.id) === effectiveSiteName) : [],
    [nodes, nodesLoaded, nodeSite, effectiveSiteName],
  );

  const filterCounts = useMemo(() => {
    const counts = new Map<EquipmentFilter, number>();
    counts.set('all', siteNodes.length);
    for (const node of siteNodes) {
      for (const family of equipmentFamilies(node)) {
        counts.set(family, (counts.get(family) || 0) + 1);
      }
    }
    return counts;
  }, [siteNodes]);

  const filteredNodes = useMemo(
    () => filter === 'all'
      ? siteNodes
      : siteNodes.filter((node) => equipmentFamilies(node).includes(filter as EquipmentFamily)),
    [siteNodes, filter],
  );

  const telemetryNow = Date.now();
  const onlineCount = nodesLoaded
    ? siteNodes.filter((node) => equipmentTelemetryState(node, telemetryNow) === TELEMETRY_ONLINE).length
    : null;
  const staleOrUnknownCount = nodesLoaded
    ? siteNodes.filter((node) => equipmentTelemetryState(node, telemetryNow) !== TELEMETRY_ONLINE).length
    : null;

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><div className="w-10 h-10 rounded-full border-2 border-[var(--color-primary)] border-t-transparent animate-spin" /></div>;
  }

  if (!topology) {
    return <div className="min-h-[60vh] flex items-center justify-center text-sm text-[var(--text-secondary)]">{t('common:availabilityUnavailable')}</div>;
  }

  return (
    <div className="space-y-5 pb-[calc(7.5rem+env(safe-area-inset-bottom,0px))] lg:pb-6" data-testid="cgm-equipment-r1">
      <SoverdiSiteSelector sites={siteNames} selected={effectiveSiteName} onSelect={(site) => { setSelectedSiteName(site); setFilter('all'); }} />

      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="mb-1 flex items-center gap-2 text-[var(--color-primary)]">
            <Boxes className="h-5 w-5" />
            <span className="text-sm font-medium">{t('agrisafeR1:nav.equipment')}</span>
          </div>
          <h1 className="text-xl font-semibold text-[var(--text-primary)] sm:text-2xl">{topology.clientName} · {effectiveSiteName}</h1>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">{t('agrisafeR1:nav.equipmentDesc')}</p>
        </div>
        <div className="rounded-full bg-[var(--bg-tertiary)] px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)]" data-testid="equipment-online-summary">
          {nodesLoaded && onlineCount !== null
            ? t('common:cgmEquipmentOnlineSummary', { online: onlineCount, total: siteNodes.length })
            : t('common:availabilityUnavailable')}
        </div>
      </div>

      {(!nodesLoaded || loadError || bindingsUnavailable || fieldBindingsError) && (
        <div className="rounded-xl border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 px-4 py-3 text-sm text-[var(--text-secondary)]" role="status" data-testid="equipment-data-warning">
          {t('common:cgmEquipmentDataWarning')}
        </div>
      )}

      <section className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <div className="rounded-xl border border-[var(--border-light)] bg-[var(--surface)] p-4">
          <div className="text-xs text-[var(--text-secondary)]">{t('agrisafeR1:dashboard.equipmentAssigned')}</div>
          <div className="mt-1 text-2xl font-semibold text-[var(--text-primary)]" data-testid="equipment-count">{nodesLoaded ? siteNodes.length : '—'}</div>
        </div>
        <div className="rounded-xl border border-[var(--border-light)] bg-[var(--surface)] p-4">
          <div className="text-xs text-[var(--text-secondary)]">{t('agrisafeR1:sectorDetail.equipmentStatus.online')}</div>
          <div className="mt-1 text-2xl font-semibold text-[var(--text-primary)]" data-testid="equipment-online-count">{onlineCount === null ? '—' : onlineCount}</div>
        </div>
        <div className="rounded-xl border border-[var(--border-light)] bg-[var(--surface)] p-4">
          <div className="text-xs text-[var(--text-secondary)]">
            {t('agrisafeR1:sectorDetail.equipmentStatus.stale')} / {t('agrisafeR1:sectorDetail.equipmentStatus.unknown')}
          </div>
          <div className="mt-1 text-2xl font-semibold text-[var(--text-primary)]" data-testid="equipment-not-recent-count">{staleOrUnknownCount === null ? '—' : staleOrUnknownCount}</div>
        </div>
        <div className="rounded-xl border border-[var(--border-light)] bg-[var(--surface)] p-4">
          <div className="text-xs text-[var(--text-secondary)]">{t('common:exploitation')}</div>
          <div className="mt-1 truncate text-lg font-semibold text-[var(--text-primary)]" title={topology.clientName}>{topology.clientName}</div>
          <div className="mt-0.5 text-xs text-[var(--text-tertiary)]">{t('common:site')} {effectiveSiteName}</div>
        </div>
      </section>

      <section className="space-y-2" aria-label={t('common:cgmEquipmentFilter')}>
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm font-semibold text-[var(--text-primary)]">{t('common:cgmEquipmentFilter')}</div>
          <div className="text-xs text-[var(--text-tertiary)]" data-testid="equipment-filter-count">
            {t('common:cgmEquipmentFilteredCount', { visible: filteredNodes.length, total: siteNodes.length })}
          </div>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="equipment-type-filters">
          {FILTERS.map((item) => {
            const active = filter === item;
            const count = filterCounts.get(item) || 0;
            return (
              <button
                key={item}
                type="button"
                onClick={() => setFilter(item)}
                aria-pressed={active}
                className={`min-h-[40px] shrink-0 rounded-full border px-3 text-sm font-semibold transition-colors ${
                  active
                    ? 'border-[var(--color-primary)] bg-[var(--color-primary)] text-white'
                    : 'border-[var(--border-light)] bg-[var(--surface)] text-[var(--text-secondary)]'
                }`}
                data-testid={`equipment-filter-${item}`}
              >
                {filterLabels[item]} · {count}
              </button>
            );
          })}
        </div>
      </section>

      <section className="overflow-hidden rounded-xl border border-[var(--border-light)] bg-[var(--surface)]">
        <div className="border-b border-[var(--border-light)] px-4 py-3">
          <h2 className="font-semibold text-[var(--text-primary)]">{t('common:cgmEquipmentField')}</h2>
          <p className="mt-1 text-xs text-[var(--text-secondary)]">{t('common:cgmEquipmentOpenHint')}</p>
        </div>

        {!nodesLoaded ? (
          <div className="px-4 py-12 text-center text-sm text-[var(--text-tertiary)]" data-testid="equipment-inventory-unavailable">
            {t('common:cgmEquipmentInventoryUnavailable')}
          </div>
        ) : filteredNodes.length === 0 ? (
          <div className="px-4 py-12 text-center text-sm text-[var(--text-tertiary)]" data-testid="equipment-filter-empty">
            {t('common:cgmEquipmentNoMatch')}
          </div>
        ) : (
          <div className="divide-y divide-[var(--border-light)]">
            {filteredNodes.map((node) => {
              const located = node.sector_id ? placement.get(node.sector_id) : undefined;
              const metrics = equipmentMetrics(node);
              const timestamp = nodeTimestamp(node);
              const telemetryState = equipmentTelemetryState(node, telemetryNow);
              const telemetryLabel = telemetryState === TELEMETRY_ONLINE
                ? t('agrisafeR1:sectorDetail.equipmentStatus.online')
                : telemetryState === TELEMETRY_STALE
                  ? t('agrisafeR1:sectorDetail.equipmentStatus.stale')
                  : t('agrisafeR1:sectorDetail.equipmentStatus.unknown');
              const commissioning = commissioningForNode(node);
              const battery = getNodeBattery(node) ?? node.battery_v ?? undefined;
              const families = equipmentFamilies(node);
              const visibleMetrics = metrics.slice(0, isExpert ? 8 : 4);
              return (
                <a
                  key={node.id}
                  href={`/r6-soverdi-equipment/${encodeURIComponent(node.id)}`}
                  className="grid gap-3 px-4 py-4 transition-colors hover:bg-[var(--bg-secondary)] lg:grid-cols-[minmax(230px,1.15fr)_minmax(175px,.9fr)_minmax(260px,1.45fr)_170px_24px] lg:items-center"
                  data-testid={`equipment-card-${node.id}`}
                  data-equipment-category={families.join(' ')}
                  data-telemetry-state={telemetryState}
                >
                  <div className="min-w-0">
                    <div className="flex min-w-0 items-center gap-2">
                      <Smartphone className="h-4 w-4 shrink-0 text-[var(--color-primary)]" />
                      <div className="truncate text-sm font-semibold text-[var(--text-primary)]" title={node.name}>{node.name}</div>
                    </div>
                    {isExpert && node.model && (
                      <div className="mt-1 truncate text-[11px] text-[var(--text-tertiary)]">{t('common:cgmEquipmentModel')}: {node.model}</div>
                    )}
                  </div>

                  <div className="min-w-0 text-xs">
                    <div className="font-medium text-[var(--text-primary)]">{families.map((family) => filterLabels[family]).join(' · ')}</div>
                    <div className="mt-1 text-[var(--text-tertiary)]">{installedNamesForNode(node.id) || (located ? `${located.siteName} · ${t('common:sectorName')} ${located.sectorCode}` : effectiveSiteName)}</div>
                    <div className="mt-1 font-medium text-[var(--text-secondary)]">
                      {t('agrisafeR1:pumping.commissioning')}: {commissioning.label}
                    </div>
                    {isExpert && commissioning.detail && (
                      <div className="mt-1 text-[11px] text-[var(--text-tertiary)]">{commissioning.detail}</div>
                    )}
                  </div>

                  <div className="min-w-0">
                    {visibleMetrics.length === 0 ? (
                      <span className="text-xs text-[var(--text-tertiary)]">{t('common:cgmEquipmentNoMetrics')}</span>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {visibleMetrics.map((metric) => (
                          <span key={`${metric.label}-${metric.value}`} className="rounded-md bg-[var(--bg-tertiary)] px-2 py-1 text-[11px] text-[var(--text-secondary)]">
                            {localizedMetricLabel(metric.label)} {localizedMetricValue(metric.value)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="text-xs lg:text-right">
                    <div className={`inline-flex items-center gap-1.5 font-semibold ${telemetryState === TELEMETRY_ONLINE ? 'text-[var(--color-success)]' : 'text-[var(--text-tertiary)]'}`}>
                      <Radio className="h-3.5 w-3.5" />{telemetryLabel}
                    </div>
                    <div className="mt-1 text-[var(--text-tertiary)]">{formatAge(timestamp)}</div>
                    {battery !== undefined && (
                      <div className="mt-1 inline-flex items-center gap-1 text-[var(--text-secondary)]"><Battery className="h-3.5 w-3.5" />{formatEquipmentValue(battery, 1, 'V')}</div>
                    )}
                  </div>

                  <ChevronRight className="hidden h-4 w-4 text-[var(--text-tertiary)] lg:block" />
                </a>
              );
            })}
          </div>
        )}
      </section>

      <div className="flex items-center justify-center gap-2 text-xs text-[var(--text-tertiary)]">
        <MapPin className="h-3.5 w-3.5" />{t('common:cgmEquipmentReadOnlyFooter')}
      </div>
    </div>
  );
};

export default CgmEquipmentR1;
